import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import React, { createContext, useContext, useCallback, useEffect, useMemo } from 'react';
import { useLocalStorage } from 'react-use';
import { Loader } from '../../components/Loader';
import {
  GetUserAccessControlDocument,
  GetUserAccessControlQuery,
  GetUserIdentitiesDocument,
  GetUserIdentitiesQuery,
  IdentityFragment,
  IdentityFragmentDoc,
} from '../../services/graphql/types/graphql';
import { getFragmentData } from '../../services/graphql/types';
import { CasdoorContext } from './casdoor.context';
import { deriveAvailableCourses } from './course-access';
import { resolveIdentitySelection, type IdentitySelectionChange } from './identity-selection';

type IdentityCourse = NonNullable<
  GetUserIdentitiesQuery['currentSchool']
>['classes'][number]['courses'][number];

export const IdentityContext = createContext<null | {
  userIdentities: GetUserIdentitiesQuery;
  uac: GetUserAccessControlQuery['user']['accessControls'];
  currentIdentity: IdentityFragment | null | undefined;

  availableClasses?: NonNullable<GetUserIdentitiesQuery['currentSchool']>['classes'];
  currentClassId: string;
  setCurrentClassId: ReturnType<typeof useLocalStorage<string>>[1];
  currentClass?: NonNullable<GetUserIdentitiesQuery['currentSchool']>['classes'][0];

  availableSchools?: GetUserIdentitiesQuery['schools'];
  currentSchoolId: string;
  setCurrentSchoolId: ReturnType<typeof useLocalStorage<string>>[1];
  currentSchool?: GetUserIdentitiesQuery['currentSchool'];

  availableSemesters?: GetUserIdentitiesQuery['semesters'];
  currentSemesterId: string | undefined;
  setCurrentSemesterId: ReturnType<typeof useLocalStorage<string>>[1];
  currentSemester?: GetUserIdentitiesQuery['semesters'][0];

  availableCourses?: IdentityCourse[];
  currentCourseId: string;
  setCurrentCourseId: ReturnType<typeof useLocalStorage<string>>[1];
  currentCourse?: IdentityCourse;
}>(null);

export const IdentityContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout } = useContext(CasdoorContext);

  // School - Fixed to specific school for teacher side
  const FIXED_SCHOOL_ID = '726e5617-5060-476c-9b56-a5f8e93834cd';
  const currentSchoolId = FIXED_SCHOOL_ID;

  const {
    loading,
    error,
    data: userIdentities,
  } = useQuery(GetUserIdentitiesDocument, {
    variables: { schoolId: FIXED_SCHOOL_ID },
    fetchPolicy: 'network-only', // Force fresh fetch, bypass cache
  });

  // Effect: 监控用户身份查询错误，将错误上报到 Sentry 监控系统
  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
    }
  }, [error]);

  // Effect: 用户身份信息加载完成后进行校验（注释掉的代码：如果当前学校为空则登出）
  useEffect(() => {
    if (loading) {
      return;
    }
    // if (isEmpty(userIdentities?.currentSchool)) {
    //   logout?.(false);
    // }
  }, [userIdentities, logout, loading]);

  const {
    loading: uacLoading,
    data: uacData,
    error: uacError,
  } = useQuery(GetUserAccessControlDocument, {
    fetchPolicy: 'network-only', // Force fresh fetch, bypass cache
  });
  const uac = useMemo(() => uacData?.user?.accessControls, [uacData]);

  const lsCurrentSchoolIdStorage = useLocalStorage('CurrentSchoolId', '');
  const [, setCurrentSchoolIdStorage] = lsCurrentSchoolIdStorage;
  // Keep setCurrentSchoolId as a no-op function to avoid breaking existing code
  const setCurrentSchoolId = () => {};

  const availableSchools = useMemo(() => userIdentities?.schools, [userIdentities]);

  const currentSchool = useMemo(() => userIdentities?.currentSchool, [userIdentities]);

  // Read the existing keys for compatibility, but expose only a coherent triple.
  const [savedSemesterId, saveSemesterId] = useLocalStorage<string>('CurrentSemesterId', '');
  const [savedClassId, saveClassId] = useLocalStorage<string>('CurrentClassId', '');
  const [savedCourseId, saveCourseId] = useLocalStorage<string>('CurrentCourseId', '');

  const availableSemesters = useMemo(() => {
    const usedIds = new Set(
      currentSchool?.classes.flatMap((clas) =>
        (clas.lessonSchedule ?? []).map((schedule) => schedule.semester?.semesterId),
      ),
    );
    return (userIdentities?.semesters ?? [])
      .filter((semester) => usedIds.has(semester.semesterId))
      .sort((a, b) => a.semesterId.localeCompare(b.semesterId));
  }, [currentSchool, userIdentities]);

  // Students can choose courses from different semesters on the course page.
  const availableCourses = useMemo(
    () => deriveAvailableCourses(currentSchool?.classes),
    [currentSchool],
  );
  const semesterIds = useMemo(
    () => availableSemesters.map((semester) => semester.semesterId),
    [availableSemesters],
  );
  const courseIds = useMemo(
    () => availableCourses.map((course) => course.courseId),
    [availableCourses],
  );
  const selection = useMemo(
    () =>
      resolveIdentitySelection(currentSchool?.classes ?? [], semesterIds, courseIds, {
        semesterId: savedSemesterId ?? '',
        classId: savedClassId ?? '',
        courseId: savedCourseId ?? '',
      }),
    [currentSchool, semesterIds, courseIds, savedSemesterId, savedClassId, savedCourseId],
  );
  const {
    semesterId: currentSemesterId,
    classId: currentClassId,
    courseId: currentCourseId,
  } = selection;

  const selectionRef = React.useRef(selection);
  selectionRef.current = selection;
  const persistSelection = useCallback(
    (next: typeof selection) => {
      saveSemesterId(next.semesterId);
      saveClassId(next.classId);
      saveCourseId(next.courseId);
    },
    [saveSemesterId, saveClassId, saveCourseId],
  );

  // One normalization effect replaces the competing class/course/semester effects.
  // The resolved values are already used above, so storage repair cannot flash a mismatch.
  useEffect(() => {
    if (currentSchool) persistSelection(selection);
  }, [currentSchool, selection, persistSelection]);

  const changeSelection = useCallback(
    (change: IdentitySelectionChange) => {
      if (!change.id || !currentSchool) return;
      const next = resolveIdentitySelection(
        currentSchool.classes,
        semesterIds,
        courseIds,
        selectionRef.current,
        change,
      );
      selectionRef.current = next;
      persistSelection(next);
    },
    [currentSchool, semesterIds, courseIds, persistSelection],
  );

  const setCurrentSemesterId: ReturnType<typeof useLocalStorage<string>>[1] = useCallback(
    (value) => {
      const id = typeof value === 'function' ? value(selectionRef.current.semesterId) : value;
      if (id) changeSelection({ type: 'semester', id });
    },
    [changeSelection],
  );
  const setCurrentClassId: ReturnType<typeof useLocalStorage<string>>[1] = useCallback(
    (value) => {
      const id = typeof value === 'function' ? value(selectionRef.current.classId) : value;
      if (id) changeSelection({ type: 'class', id });
    },
    [changeSelection],
  );
  const setCurrentCourseId: ReturnType<typeof useLocalStorage<string>>[1] = useCallback(
    (value) => {
      const id = typeof value === 'function' ? value(selectionRef.current.courseId) : value;
      if (id) changeSelection({ type: 'course', id });
    },
    [changeSelection],
  );

  const currentSemester = availableSemesters.find(
    (semester) => semester.semesterId === currentSemesterId,
  );
  const currentClass = currentSchool?.classes.find((clas) => clas.classId === currentClassId);
  const currentCourse = availableCourses.find((course) => course.courseId === currentCourseId);
  const currentIdentity = getFragmentData(IdentityFragmentDoc, currentClass?.selfIdentity);
  const availableClasses = useMemo(
    () =>
      (currentSchool?.classes ?? [])
        .filter(
          (clas) =>
            !currentSemesterId ||
            clas.lessonSchedule?.some(
              (schedule) => schedule.semester?.semesterId === currentSemesterId,
            ),
        )
        .sort((a, b) => a.classId.localeCompare(b.classId)),
    [currentSchool, currentSemesterId],
  );

  if (!userIdentities || !currentClassId || !currentSchoolId || !currentCourseId || !uac) {
    console.info('userIdentities', userIdentities);
    console.info('currentClassId', currentClassId);
    console.info('currentSchoolId', currentSchoolId);
    console.info('currentCourseId', currentCourseId);
    console.info('uac', uac);
    return <Loader source="IdentityProvider" fullscreen />;
  }

  return (
    <IdentityContext.Provider
      value={{
        userIdentities,
        uac,
        currentIdentity,

        currentClassId,
        currentClass,
        setCurrentClassId,
        availableClasses,

        currentSchoolId,
        currentSchool,
        setCurrentSchoolId,
        availableSchools,

        currentSemesterId,
        currentSemester,
        setCurrentSemesterId,
        availableSemesters,

        currentCourseId,
        currentCourse,
        setCurrentCourseId,
        availableCourses,
      }}
    >
      {children}
    </IdentityContext.Provider>
  );
};
